import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

import {
  setOperationStatus,
  UI_OPERATION_STATUS_SUCCESS,
  UI_OPERATION_STATUS_ERROR,
} from '@uncinc/react-drupal-core';

import type { ImmutableRootState } from '@uncinc/react-drupal-core/types';
import { Button, FormikWrapper, FormTextField } from '@uncinc/uncinc-react-kitchen-sink';

import { createOrUpdateUserProfile } from '../../../../store/actions/Profile';
import useCurrentUser from '../../../../hooks/useCurrentUser';

import './style.scss';

// Separates the address fields from the general fields.
const separateFields = (values: any) => {
  const fieldEntries = Object.entries(values).reduce(
    (acc: { fields: { [key: string]: any }, address: { [key: string]: any } }, [key, value]) => {
      if (key.startsWith("field_")) {
        acc.fields[key] = value;
      } else {
        acc.address[key] = value;
      }
      return acc;
    },
    { fields: {}, address: {} },
  );

  return fieldEntries;
};

const BillingAccountPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { profile } = useCurrentUser() ?? {};
  const profileUpdateStatus = useSelector<ImmutableRootState<string>, string>((state) => state.getIn(['ui', 'profile', 'operations', 'update', 'status'], ''));

  let initialValues = { country_code: 'NL', field_billing_email_address: '', field_billing_reference: '' };
  if (profile?.field_billing_address) {
    initialValues = {
      ...initialValues,
      ...profile.field_billing_address[0],
    };
  }
  if (profile?.field_billing_email_address?.[0]?.value) {
    initialValues.field_billing_email_address = profile.field_billing_email_address[0].value;
  }
  if (profile?.field_billing_reference?.[0]?.value) {
    initialValues.field_billing_reference = profile.field_billing_reference[0].value;
  }

  const AddressValidationSchema = Yup.object().shape({
    country_code: Yup.string(),
    given_name: Yup.string().required(t('validation_messages.signup.given_name')),
    family_name: Yup.string().required(t('validation_messages.signup.family_name')),
    organization: Yup.string().when('business_option', {
      is: (val: any) => val === 'business',
      then: Yup.string().required(t('validation_messages.signup.billing_organisation')).max(60),
      otherwise: Yup.string().notRequired(),
    }),
    locality: Yup.string().required(t('validation_messages.signup.billing_locality')),
    postal_code: Yup.string().required(t('validation_messages.signup.billing_postal_code')).matches(/^[1-9][0-9]{3} ?(?!sa|sd|ss)[a-zA-Z ._-]{2}$/, t('validation_messages.signup.billing_postal_code_invalid')),
    address_line1: Yup.string().required(t('validation_messages.signup.billing_address_line1')),
    address_line2: Yup.string().trim().required(t('validation_messages.signup.billing_address_line2')).matches(/^(?:[0-9]{1})/, t('validation_messages.signup.housenumber.matches')),
    field_billing_email_address: Yup.string().email().trim().notRequired(),
    field_billing_reference: Yup.string().trim().notRequired(),
  });

  const onFormSubmit = (values: any) => {
    const data = separateFields(values);

    dispatch(createOrUpdateUserProfile({
      field_billing_address: [data.address],
      ...data.fields,
    }) as any);
  };

  // Listens to the update status and shows a success or error toast message.
  useEffect(() => {
    if (profileUpdateStatus !== null) {
      if (profileUpdateStatus === UI_OPERATION_STATUS_SUCCESS) {
        toast.success(t('mijn_vnl.overview.profile_success'));
      } else if (profileUpdateStatus === UI_OPERATION_STATUS_ERROR) {
        toast.error(t('mijn_vnl.overview.profile_error'));
      }
      dispatch(setOperationStatus('profile', 'update', null, null));
    }
  }, [profileUpdateStatus, dispatch, t]);

  return (
    <div className="AccountBilling">
      <FormikWrapper
        onSubmit={onFormSubmit}
        initialValues={initialValues}
        validationSchema={AddressValidationSchema}
        key={`account-billing-${Object.keys(initialValues).length}`}
      >
        <div className="row">
          <div className="col-12">
            <FormTextField
              title={t('address.organization_no_optional')}
              fieldName="organization"
            />
          </div>
        </div>
        <div className="row">
          <div className="col-12 col-sm-6">
            <FormTextField
              title={t('address.first_name')}
              fieldName="given_name"
            />
          </div>
          <div className="col-12 col-sm-6">
            <FormTextField
              title={t('address.family_name')}
              fieldName="family_name"
            />
          </div>
        </div>
        <div className="row">
          <div className="col-12">
            <FormTextField
              title={t('address.postal_code')}
              fieldName="postal_code"
            />
          </div>
        </div>
        <div className="row">
          <div className="col-12 col-lg-8">
            <FormTextField
              title={t('address.address_line1')}
              fieldName="address_line1"
            />
          </div>
          <div className="col-12 col-lg-4">
            <FormTextField
              title={t('address.address_line2')}
              fieldName="address_line2"
            />
          </div>
        </div>
        <div className="row">
          <div className="col-12">
            <FormTextField
              title={t('address.locality')}
              fieldName="locality"
            />
          </div>
        </div>
        <div className="row">
          <div className="col-12">
            <FormTextField
              title={t('mijn_vnl.billing.email')}
              fieldName="field_billing_email_address"
            />
          </div>
        </div>
        <div className="row">
          <div className="col-12">
            <FormTextField
              title={t('mijn_vnl.billing.reference')}
              fieldName="field_billing_reference"
            />
          </div>
        </div>
        <div className="row">
          <div className="col-6 col-md-4">
            <Button
              className="Button--block"
              type="submit"
            >
              {t('general.save')}
            </Button>
          </div>
        </div>
      </FormikWrapper>
    </div>
  );
};

export default BillingAccountPage;
